import { ReactNode, createContext, useContext, useCallback } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import '../styles/toast-animations.css';

type ToastContextType = {
   showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
};

type ToastProviderProps = {
   children: ReactNode;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: ToastProviderProps) => {
   const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
      // Ultra-modern toast styling with type-appropriate colors
      switch (type) {
         case 'success':
            toast.success(message, {
               duration: 4000,
               position: 'top-right',
               style: {
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 50%, #059669 100%)',
                  color: '#ffffff',
                  padding: '20px 26px',
                  borderRadius: '16px',
                  fontSize: '15px',
                  fontWeight: '600',
                  boxShadow: '0 20px 60px -12px rgba(16, 185, 129, 0.6), 0 10px 30px -10px rgba(5, 150, 105, 0.4)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  minWidth: '320px',
                  letterSpacing: '0.02em',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
               },
               iconTheme: {
                  primary: '#ffffff',
                  secondary: '#10b981',
               },
            });
            break;
         case 'error':
            toast.error(message, {
               duration: 5000,
               position: 'top-right',
               style: {
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 50%, #c92a2a 100%)',
                  color: '#ffffff',
                  padding: '20px 26px',
                  borderRadius: '16px',
                  fontSize: '15px',
                  fontWeight: '600',
                  boxShadow: '0 20px 60px -12px rgba(238, 90, 111, 0.5), 0 10px 30px -10px rgba(255, 107, 107, 0.3)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  minWidth: '320px',
                  letterSpacing: '0.02em',
               },
               iconTheme: {
                  primary: '#ffffff',
                  secondary: '#ff6b6b',
               },
            });
            break;
         case 'info':
         default:
            toast(message, {
               duration: 4000,
               position: 'top-right',
               icon: '💡',
               style: {
                  background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 50%, #4c51bf 100%)',
                  color: '#ffffff',
                  padding: '20px 26px',
                  borderRadius: '16px',
                  fontSize: '15px',
                  fontWeight: '600',
                  boxShadow: '0 20px 60px -12px rgba(102, 126, 234, 0.5), 0 10px 30px -10px rgba(90, 103, 216, 0.3)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.18)',
                  minWidth: '320px',
                  letterSpacing: '0.02em',
               },
            });
            break;
      }
   }, []);

   return (
      <ToastContext.Provider value={{ showToast }}>
         {children}
         <Toaster 
            toastOptions={{
               // Global toast options with smooth animations
               className: '',
               duration: 4000,
               style: {
                  maxWidth: '500px',
               },
            }}
            containerStyle={{
               top: 80,
               right: 20,
               zIndex: 99999,
            }}
         />
      </ToastContext.Provider>
   );
};

export const useToast = () => {
   const context = useContext(ToastContext);

   if (context === undefined) {
      throw new Error('useToast must be used within a ToastProvider');
   }

   return context;
};
